import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';
import { rememberWebsiteUrl, useShareImage } from './share-image';
import { discardUpload, uploadFile } from './uploads';
import type { LocaleProgress } from './apps';

/**
 * The blog, as the admin sees it: drafts included, every language at once.
 *
 * The screen read four rows out of a bundled design fixture until this query
 * existed — and the two disagreed about everything, including the addresses:
 * the file's slugs were short (`why-on-device`) while the ones the website
 * serves are the whole title (`why-we-run-the-model-on-your-device`). That
 * fixture is gone; this is the only source now.
 */

interface PostTopic {
  id: string;
  slug: string;
  name: Record<string, string>;
}

export interface AdminPost {
  id: string;
  slug: string;
  /** Null while nobody has decided what the post is about. */
  topic: PostTopic | null;
  /** The byline as shown: the linked person's name, or the words the post holds. */
  author: string;
  /** The account the byline is, or null for a post nobody has linked to one. */
  authorId: string | null;
  /** Whether this is the post at the top of the blog — at most one is. */
  featured: boolean;
  /** Null while it is a draft; ahead of now means scheduled. */
  publishedAt: string | null;
  /**
   * When it first went public, or null if it never has.
   *
   * Not the same question as `publishedAt`, and the editor needs this one: a
   * post taken back down is a draft by that column, and the links from when it
   * was up still exist — so this is what decides whether the address is still
   * the studio's to change.
   */
  firstPublishedAt: string | null;
  title: Record<string, string>;
  excerpt: Record<string, string>;
  /** Markdown, one string per language — see the note on the column. */
  body: Record<string, string>;
  /** Where the cover is, or null while the post has none. */
  coverUrl: string | null;
  /**
   * What the cover shows, per language.
   *
   * One picture for every language, a description in each: translating a
   * photograph would mean commissioning a second one, while the sentence read
   * aloud to a Japanese reader does have to be in Japanese.
   */
  coverAlt: Record<string, string>;
  /**
   * What a search result and a shared link say instead of the title and the
   * excerpt, per language; blank means the automatic text. And the picture a
   * share unfurls with instead of the cover, when one was chosen.
   */
  seoTitle: Record<string, string>;
  seoDescription: Record<string, string>;
  shareImageUrl: string | null;
  locales: LocaleProgress[];
  updatedAt: string;
}

/** Rows on one page of the table, matching the Apps table's page. */
export const POSTS_PER_PAGE = 50;

const ADMIN_POSTS = gql`
  query AdminPosts($search: String, $limit: Int, $offset: Int) {
    adminPosts(search: $search, limit: $limit, offset: $offset) {
      items {
        id
        slug
        topic {
          id
          slug
          name
        }
        author
        authorId
        publishedAt
        title
        locales {
          locale
          translated
          total
        }
        updatedAt
      }
      total
      notYetLive
    }
  }
`;

/** One page of the Blog table, with the count the pager needs. */
export interface AdminPostList {
  items: AdminPost[];
  /** Every post matching the search, not the size of this page. */
  total: number;
  /** Drafts and scheduled posts — counted over every page, not this one. */
  notYetLive: number;
}

/**
 * One page of the Blog table.
 *
 * ⚠️ **The search moved to the server with the paging, and had to.** It used to
 * filter the fetched array in the browser, which was right while the whole
 * table was fetched. Page one at a time and that same code searches only the
 * page on screen — so a post two pages down reports as not existing, which is
 * a wrong answer delivered confidently and is worse than the slowness the
 * paging was for.
 *
 * `page` is 1-based, because it is a page number somebody reads off a button.
 */
export const useAdminPosts = (filters: { search?: string; page?: number } = {}) =>
  useQuery({
    // The search and the page are in the key, so changing either refetches
    // rather than showing the previous answer under the new heading.
    queryKey: ['adminPosts', filters.search ?? '', filters.page ?? 1],
    queryFn: async () =>
      (
        await gqlClient.request<{ adminPosts: AdminPostList }>(ADMIN_POSTS, {
          search: filters.search?.trim() || null,
          limit: POSTS_PER_PAGE,
          offset: ((filters.page ?? 1) - 1) * POSTS_PER_PAGE,
        })
      ).adminPosts,
    /*
     * Keep the rows on screen while the next search runs. Otherwise every
     * refined search empties the table and replaces it with "Loading", so the
     * thing being narrowed down disappears exactly while somebody is looking
     * at it. The apps list does the same.
     */
    placeholderData: keepPreviousData,
  });

/**
 * Which of the three states a post is in.
 *
 * Read from the one column that decides it. A separate flag would be a second
 * thing to keep true, and the two would disagree the first time one was set
 * without the other.
 */
export type PostStatus = 'Published' | 'Scheduled' | 'Draft';

export const statusOf = (publishedAt: string | null, now = Date.now()): PostStatus => {
  if (!publishedAt) return 'Draft';
  return new Date(publishedAt).getTime() > now ? 'Scheduled' : 'Published';
};

/**
 * The title in English, or in whatever language it was written in.
 *
 * A post whose English title is still empty is not a nameless row in the list —
 * showing a blank there would make it unfindable in the one screen that exists
 * to find it. Falls back to any language that has one, and says so by returning
 * the locale alongside.
 */
export const listTitle = (title: Record<string, string>): { text: string; locale: string } => {
  const english = title.en?.trim();
  if (english) return { text: english, locale: 'en' };

  const written = Object.entries(title).find(([, value]) => value?.trim());
  return written
    ? { text: written[1].trim(), locale: written[0] }
    : { text: 'Untitled', locale: 'en' };
};

/** "18 Aug 2026", or nothing at all for a draft that has no date. */
export const postDate = (publishedAt: string | null): string =>
  publishedAt
    ? new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'UTC' }).format(
        new Date(publishedAt),
      )
    : '—';

const POST_FIELDS = `
  id
  slug
  topic {
    id
    slug
    name
  }
  author
  authorId
  featured
  publishedAt
  firstPublishedAt
  title
  excerpt
  body
  coverUrl
  coverAlt
  seoTitle
  seoDescription
  shareImageUrl
  locales {
    locale
    translated
    total
  }
  updatedAt
`;

const ADMIN_POST = gql`
  query AdminPost($id: ID!) {
    adminPost(id: $id) {
      ${POST_FIELDS}
    }
    websiteUrl
  }
`;

export const useAdminPost = (id: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['adminPost', id],
    queryFn: async () => {
      const data = await gqlClient.request<{ adminPost: AdminPost | null; websiteUrl?: string }>(
        ADMIN_POST,
        { id },
      );
      rememberWebsiteUrl(queryClient, data.websiteUrl);
      return data.adminPost;
    },
    /*
     * The shell calls this on every screen in the admin, passing an empty id on
     * the ones that are not a post editor — so without this it asked the server
     * for a post named `""` on every page load. Postgres cannot parse that as a
     * uuid, so the answer was a 500 with a stack trace, once per navigation,
     * swallowed silently because the shell ignores the result unless it is
     * editing a post.
     *
     * The server no longer raises on it (`PostService.findOneForAdmin` guards
     * the same way the app reader always has), and this stays anyway: a request
     * whose answer is known before it is sent is a request worth not sending.
     * `useAdminApp` has had this line from the start.
     */
    enabled: id.length > 0,
  });
};

const CREATE_POST = gql`
  mutation CreatePost($input: CreatePostInput!) {
    createPost(input: $input) {
      id
      slug
    }
  }
`;

export interface CreatePostInput {
  slug: string;
  title: string;
  topicId?: string | null;
  /** A first draft, in English. Omitted by "New post", sent by the release button. */
  excerpt?: string;
  body?: string;
}

export const useCreatePost = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePostInput) => {
      const data = await gqlClient.request<{ createPost: { id: string; slug: string } }>(
        CREATE_POST,
        { input },
      );
      return data.createPost;
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['adminPosts'] }),
  });
};

/** The half of a post that is the same in every language. */
export interface UpdatePostInput {
  slug?: string;
  topicId?: string | null;
  /** The account to sign it with. Sent only when somebody picked one. */
  authorId?: string;
  /**
   * Put this post at the top of the blog, or take it down from there.
   *
   * A flag, not the instant behind it: the server stamps the time, so two posts
   * cannot be marked at the same moment and none can be marked in the future.
   */
  featured?: boolean;
  /**
   * The instant, or null to put it back to a draft.
   *
   * Nullable *and* optional, which are different things: absent means "leave it
   * alone", an explicit null means "this is a draft again". The editor sends
   * the whole set, so it always sends one of the two.
   */
  publishedAt?: string | null;
}

const UPDATE_POST = gql`
  mutation UpdatePost($id: ID!, $input: UpdatePostInput!) {
    updatePost(id: $id, input: $input) {
      ${POST_FIELDS}
    }
  }
`;

const UPDATE_POST_CONTENT = gql`
  mutation UpdatePostContent($id: ID!, $locale: ContentLocale!, $content: PostContentInput!) {
    updatePostContent(id: $id, locale: $locale, content: $content) {
      ${POST_FIELDS}
    }
  }
`;

export interface PostContentInput {
  title?: string;
  excerpt?: string;
  /** Markdown. One string, not a list of paragraphs. */
  body?: string;
  /** What the cover shows, in the language being saved. */
  coverAlt?: string;
  /** What a search result and a share say instead of the title and the excerpt; blank = automatic. */
  seoTitle?: string;
  seoDescription?: string;
}

/**
 * One save, two mutations, in a fixed order.
 *
 * The shared fields go first and the language-specific ones second, because the
 * first is the one that can be refused — a slug the studio no longer owns, an
 * address already taken. Writing the translation first and then failing would
 * leave the post half-saved, with the screen reporting an error over text that
 * did reach the database.
 *
 * ⚠️ `locale` is upper-cased here. `ContentLocale` is a GraphQL enum whose
 * members are EN, VI, JA, KO, ZH; a mocked test answers `en` happily and the
 * running server does not.
 */
export const useSavePost = (id: string) => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shared,
      locale,
      content,
    }: {
      shared: UpdatePostInput;
      locale: string;
      content: PostContentInput;
    }) => {
      await gqlClient.request<{ updatePost: AdminPost }>(UPDATE_POST, { id, input: shared });
      const data = await gqlClient.request<{ updatePostContent: AdminPost }>(UPDATE_POST_CONTENT, {
        id,
        locale: locale.toUpperCase(),
        content,
      });
      return data.updatePostContent;
    },
    onSuccess: (post) => {
      /*
       * The server's copy replaces the local one rather than the form's own
       * values being kept: a trimmed name, or a field another tab changed,
       * would otherwise stay wrong on screen until somebody reloaded — and the
       * screen would be showing something nobody stored.
       */
      client.setQueryData(['adminPost', id], post);
      // The list carries the title, topic, date and languages this may change.
      void client.invalidateQueries({ queryKey: ['adminPosts'] });
    },
  });
};

const SET_POST_COVER = gql`
  mutation SetPostCover($id: ID!, $fileId: ID) {
    setPostCover(id: $id, fileId: $fileId) {
      id
      coverUrl
      coverAlt
      updatedAt
    }
  }
`;

/**
 * Sets the cover, or takes it off when given nothing.
 *
 * Outside the Save button on purpose, the same way an app's screenshots are.
 * The bytes go up in a request of their own, and the id that comes back is
 * what the row points at — holding that in a draft until Save would leave
 * stored bytes nothing references every time a save failed or a tab closed.
 *
 * The trade is that the picture changes at once and Remove cannot be undone by
 * not saving. That is worth saying out loud rather than discovering.
 */
export const usePostCover = (id: string) => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (file: File | null) => {
      const stored = file ? await uploadFile(file) : null;
      try {
        const data = await gqlClient.request<{
          setPostCover: {
            coverUrl: string | null;
            coverAlt: Record<string, string>;
            updatedAt: string;
          };
        }>(SET_POST_COVER, { id, fileId: stored?.id ?? null });
        return data.setPostCover;
      } catch (error) {
        // The bytes are already stored when the mutation refuses, and nothing
        // points at them.
        if (stored) await discardUpload(stored.id);
        throw error;
      }
    },
    onSuccess: ({ coverUrl, coverAlt, updatedAt }) => {
      /*
       * Both, because removing a cover clears its description on the server —
       * a sentence with no picture would be read aloud about whatever the next
       * one turns out to be. Taking only `coverUrl` here would leave the words
       * on screen after the picture they describe had gone. And `updatedAt`,
       * which addresses the share preview: the website draws the cover into
       * that picture and keeps the drawing under the old address for an hour.
       */
      client.setQueryData<AdminPost>(['adminPost', id], (current) =>
        current ? { ...current, coverUrl, coverAlt, updatedAt } : current,
      );
    },
  });
};

/**
 * How many words the body has, for the count the design puts beside it.
 *
 * The Markdown is taken off first. `##`, `>` and a fence are not words, and
 * counting them makes the number climb every time somebody adds a heading —
 * which is the opposite of what the writer is watching it for.
 */
export const wordCount = (body: string): number =>
  body
    .split('\n')
    .filter((line) => !/^\s*```/.test(line))
    .map((line) => line.replace(/^\s*(#{1,6}|>|[-*+]|\d+\.)\s+/, ''))
    .join(' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

const SET_POST_SHARE_IMAGE = gql`
  mutation SetPostShareImage($id: ID!, $fileId: ID) {
    setPostShareImage(id: $id, fileId: $fileId) {
      id
      shareImageUrl
      updatedAt
    }
  }
`;

/** The picture a link to this post unfurls with, set and cleared like the cover. */
export const usePostShareImage = (id: string) =>
  useShareImage<AdminPost>({
    mutation: SET_POST_SHARE_IMAGE,
    variables: { id },
    queryKey: ['adminPost', id],
  });
